import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FeatureFlag } from '../../models/feature-flag-models';
import { FeatureFlagDetailsComponent } from '../feature-flag-details/feature-flag-details.component';
import { sortEnvironmentsSafely } from '../../utils/environment-order';

@Component({
  selector: 'app-feature-flag-table',
  standalone: true,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    FeatureFlagDetailsComponent
],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  template: `
    <mat-form-field class="search-field">
      <mat-label>Filter flags</mat-label>
      <input matInput type="search" [(ngModel)]="searchTerm" (ngModelChange)="onSearchChange($event)" placeholder="Search by key, name, description, or tag" />
    </mat-form-field>

    <div class="table-wrapper">
      <div class="table-container">
        <table mat-table
          [dataSource]="filteredFlags"
          class="feature-flag-table"
          multiTemplateDataRows>
        <!-- Key Column -->
        <ng-container matColumnDef="key">
          <th mat-header-cell *matHeaderCellDef>Key</th>
          <td mat-cell *matCellDef="let flag" (click)="expandRow(flag)">
            <span class="clickable">{{ flag.key }}</span>
          </td>
        </ng-container>
    
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Name</th>
          <td mat-cell *matCellDef="let flag">{{ flag.name }}</td>
        </ng-container>
    
        <ng-container matColumnDef="description">
          <th mat-header-cell *matHeaderCellDef>Description</th>
          <td mat-cell *matCellDef="let flag">{{ flag.description }}</td>
        </ng-container>
    
        @for (env of environments; track env) {
          <ng-container [matColumnDef]="env">
            <th mat-header-cell *matHeaderCellDef>{{ env }}</th>
            <td mat-cell *matCellDef="let flag">
              <mat-slide-toggle
                [checked]="flag.environments[env]?.on"
                [disabled]="loading || flag.environments[env]?.archived"
                (change)="onToggle(flag.key, env, $event.checked)"
                [matTooltip]="flag.environments[env]?.archived ? 'Environment is archived' : ''"
                >
              </mat-slide-toggle>
            </td>
          </ng-container>
        }
    
        <ng-container matColumnDef="tags">
          <th mat-header-cell *matHeaderCellDef>Tags</th>
          <td mat-cell *matCellDef="let flag">
            <mat-chip-set>
              @for (tag of flag.tags; track tag) {
                <mat-chip>{{ tag }}</mat-chip>
              }
            </mat-chip-set>
          </td>
        </ng-container>
    
        <!-- Expanded Content Column -->
        <ng-container matColumnDef="expandedDetail">
          <td mat-cell *matCellDef="let flag" [attr.colspan]="displayedColumns.length">
            <div class="flag-detail"
              [@detailExpand]="flag === expandedFlag ? 'expanded' : 'collapsed'">
              @if (flag === expandedFlag) {
                <app-feature-flag-details
                  [featureFlag]="flag"
                  [apiKey]="apiKey">
                </app-feature-flag-details>
              }
            </div>
          </td>
        </ng-container>
    
        <!-- Add a footer column definition -->
        <ng-container matColumnDef="footerRow">
          <td mat-footer-cell *matFooterCellDef [attr.colspan]="displayedColumns.length">
            Showing {{ filteredFlags.length }} of {{ (featureFlags || []).length }} feature flags
          </td>
        </ng-container>

        <!-- Row definitions -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"
          class="flag-row"
          [class.expanded-row]="expandedFlag === row">
        </tr>
        <tr mat-row *matRowDef="let row; columns: ['expandedDetail']"
          class="detail-row"
          [@detailExpand]="row === expandedFlag ? 'expanded' : 'collapsed'">
        </tr>
        <tr mat-footer-row *matFooterRowDef="['footerRow']"></tr>

        </table>
      </div>

      @if (loading) {
        <div class="loading-overlay">
          <mat-spinner diameter="50"></mat-spinner>
        </div>
      }
    </div>
    `,
  styles: [`
    :host {
      /* Lets the search field sit inline with the parent's project selector
         (flex row in feature-flag-manager.component.ts .controls-row) while
         the table itself still wraps to its own line via .table-wrapper's
         flex-basis. */
      display: contents;
    }

    .search-field {
      width: 320px;
    }

    .table-wrapper {
      position: relative;
      min-height: 200px;
      flex-basis: 100%;
    }

    .table-container {
      /* Scrolls independently so the sticky header works; kept separate from
         .table-wrapper so .loading-overlay (a sibling, not a child) stays
         pinned to the visible viewport instead of scrolling away with it. */
      max-height: 70vh;
      overflow: auto;
    }

    .feature-flag-table {
      width: 100%;
    }

    th.mat-mdc-header-cell {
      background: white;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    mat-chip-set {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    mat-chip {
      font-size: 12px;
    }

    .flag-row {
      cursor: pointer;
    }

    .flag-row:not(.expanded-row):hover {
      background: #f5f5f5;
    }

    .flag-row.expanded-row {
      border-bottom-color: transparent;
    }

    .flag-detail {
      overflow: hidden;
    }

    .detail-row {
      height: 0;
    }

    .clickable {
      color: #1976d2;
      text-decoration: underline;
      cursor: pointer;
    }

    tr.detail-row {
      height: 0;
    }

    tr.flag-row:not(.expanded-row):hover {
      background: whitesmoke;
    }

    tr.flag-row:not(.expanded-row):active {
      background: #efefef;
    }

    tr.mat-mdc-footer-row {
      font-weight: 500;
      background-color: #fafafa;
    }

    tr.mat-mdc-footer-row td {
      padding: 16px;
      text-align: right;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
    }

    .mat-mdc-footer-cell {
      padding: 16px;
      text-align: right;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
    }
  `]
})
export class FeatureFlagTableComponent {
  @Input() featureFlags: FeatureFlag[] | null = null;
  @Input() loading = false;
  @Input() apiKey = '';
  // Only used to detect a project switch (vs. a same-project flag refresh
  // after a toggle) so the search term can be cleared without wiping it out
  // every time a toggle reloads the list.
  @Input() projectKey = '';
  @Output() toggleFlag = new EventEmitter<{
    flagKey: string;
    environment: string;
    enabled: boolean;
  }>();

  environments: string[] = [];
  displayedColumns: string[] = ['key', 'name', 'description'];
  expandedFlag: FeatureFlag | null = null;
  searchTerm = '';
  filteredFlags: FeatureFlag[] = [];

  // Debounced so filtering a 500+ row list doesn't run on every keystroke;
  // bump this if it still feels laggy on very large flag lists.
  private readonly searchTerm$ = new Subject<string>();

  constructor() {
    this.searchTerm$.pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.applyFilter());
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.['projectKey'] && !changes['projectKey'].isFirstChange()) {
      // Route through onSearchChange (not a direct field assignment) so the
      // debounce stream's distinctUntilChanged memory also resets - otherwise
      // re-typing the same term in the new project would be silently
      // suppressed as a "no change" from the stream's point of view.
      this.onSearchChange('');
    }
    if (this.featureFlags && this.featureFlags.length > 0) {
      // Get unique environments from the first flag
      this.environments = sortEnvironmentsSafely(Object.keys(this.featureFlags[0].environments));
      // Update displayed columns
      this.displayedColumns = ['key', 'name', 'description', ...this.environments, 'tags'];
    }
    this.applyFilter();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerm$.next(term);
  }

  private applyFilter(): void {
    const flags = this.featureFlags || [];
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredFlags = !term ? flags : flags.filter((flag) =>
      flag.key.toLowerCase().includes(term) ||
      flag.name.toLowerCase().includes(term) ||
      flag.description?.toLowerCase().includes(term) ||
      flag.tags?.some((tag) => tag.toLowerCase().includes(term))
    );
  }

  onToggle(flagKey: string, environment: string, enabled: boolean): void {
    this.toggleFlag.emit({ flagKey, environment, enabled });
  }

  expandRow(flag: FeatureFlag): void {
    this.expandedFlag = this.expandedFlag === flag ? null : flag;
  }
}
